import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Undo2, Redo2 } from 'lucide-react';
import TakeNote from './TakeNote';
import { useT } from '../lib/LanguageContext';

const TRANSITION_MS = 320;
// Theme-aware via the CSS variables in index.css. FAB_COLOR is the FAB's own
// color, so the box reads as the FAB itself growing.
const FAB_COLOR = 'rgb(var(--color-keep-accent))';
const SURFACE_COLOR = 'rgb(var(--color-keep-surface))';

export default function NoteComposeModal({ onClose, origin, ...takeNoteProps }) {
  const t = useT();
  const [history, setHistory] = useState({ canUndo: false, canRedo: false, undo: () => {}, redo: () => {} });
  // 'opening' -> 'open' (grow from the FAB) -> 'closing' (shrink back to it)
  const [phase, setPhase] = useState(origin ? 'opening' : 'open');
  const closingRef = useRef(false);

  useEffect(() => {
    if (phase !== 'opening') return;
    // A single rAF isn't reliably enough here: React can commit the
    // 'opening' (at-the-FAB) styles and the following 'open' (full-screen)
    // styles within the same animation frame, in which case the browser
    // never paints the starting position and the transition has nothing to
    // animate from — it just pops open. Waiting two frames guarantees the
    // first paint (pinned at the FAB) actually lands before we flip the
    // target, so the transition has a real start point to grow from.
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPhase('open'));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [phase]);

  const close = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (!origin) {
      onClose();
      return;
    }
    setPhase('closing');
    setTimeout(onClose, TRANSITION_MS);
  };

  const originTransform = (() => {
    if (!origin) return null;
    // clientWidth/clientHeight (not window.innerWidth/innerHeight, which
    // include the scrollbar gutter and drift from the mobile toolbar) match
    // what `fixed inset-0` actually renders at, so the box lands exactly
    // over the FAB instead of a few px off.
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const scaleX = origin.width / vw;
    const scaleY = origin.height / vh;
    return `translate(${origin.left}px, ${origin.top}px) scale(${scaleX}, ${scaleY})`;
  })();

  const open = phase === 'open';
  const boxStyle = originTransform
    ? {
        transformOrigin: 'top left',
        // A gentler decelerate curve than the old (0.32, 0.72, 0, 1) — that
        // one dumped most of the growth into the first ~15% of the
        // duration, which read as a pop-then-crawl rather than a smooth
        // grow. This one spreads the motion out more evenly.
        transition: `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1), border-radius ${TRANSITION_MS}ms ease, background-color ${TRANSITION_MS}ms ease`,
        transform: open ? 'translate(0px, 0px) scale(1, 1)' : originTransform,
        borderRadius: open ? '0px' : '9999px',
        backgroundColor: open ? SURFACE_COLOR : FAB_COLOR,
      }
    : undefined;

  // The box itself stays fully opaque throughout (it's always painted, at
  // the FAB's exact spot on the very first frame) so it reads as the FAB
  // growing rather than a white blob popping in mid-grow. Only the header
  // + form content cross-fades, in after the box has mostly grown, out
  // immediately on close so text doesn't shrink along with the box.
  const contentStyle = originTransform
    ? {
        opacity: open ? 1 : 0,
        transition: open
          ? `opacity ${Math.round(TRANSITION_MS * 0.6)}ms ease ${Math.round(TRANSITION_MS * 0.35)}ms`
          : `opacity ${Math.round(TRANSITION_MS * 0.35)}ms ease`,
      }
    : undefined;

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col md:hidden overflow-hidden ${
        originTransform ? '' : 'bg-keep-surface'
      }`}
      style={boxStyle}
      data-testid="note-compose-modal"
    >
      <div className="flex-1 min-h-0 flex flex-col" style={contentStyle}>
        <div className="flex items-center justify-between px-1.5 h-14 flex-shrink-0">
          <button
            type="button"
            onClick={close}
            className="p-2 rounded-full hover:bg-keep-hover"
            aria-label={t('common.close')}
            data-testid="note-compose-close"
          >
            <ArrowLeft className="w-5 h-5 text-keep-textMuted" />
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={history.undo}
              disabled={!history.canUndo}
              className="p-2 rounded-full hover:bg-keep-hover disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t('takeNote.undo')}
              data-testid="note-compose-undo"
            >
              <Undo2 className="w-5 h-5 text-keep-textMuted" />
            </button>
            <button
              type="button"
              onClick={history.redo}
              disabled={!history.canRedo}
              className="p-2 rounded-full hover:bg-keep-hover disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t('takeNote.redo')}
              data-testid="note-compose-redo"
            >
              <Redo2 className="w-5 h-5 text-keep-textMuted" />
            </button>
          </div>
        </div>
        <TakeNote {...takeNoteProps} forceExpanded onClose={close} onHistoryChange={setHistory} />
      </div>
    </div>
  );
}
